'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Turnstile } from '@marsidev/react-turnstile';
import { submitCruiseContact, submitOrganizerContact } from '@/actions/contact';

interface CTAFormProps {
  cruiseId?: string;
  organizerSlug?: string;
  recipientName?: string;
  recipientType?: 'cruise' | 'organizer';
  onSuccess?: () => void;
}

const Spinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

export default function CTAForm({ cruiseId, organizerSlug, recipientName, recipientType = 'cruise', onSuccess }: CTAFormProps) {
  const t = useTranslations();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    message?: string;
    turnstile?: string;
  }>({});

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check if Turnstile is required (only if site key is configured AND not in dev environment)
  const turnstileConfig = (() => {
    // Check for site key from both build-time and runtime config
    // ConfigMap may have NEXT_PUBLIC_CLOUDFLARE_SITE_KEY (could be empty), 
    // but the actual key is NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY from secret (embedded at build time)
    let siteKey: string | undefined;
    
    if (typeof window !== 'undefined' && window._ENV_) {
      // Check runtime config first (but skip if empty string)
      const runtimeKey = window._ENV_.NEXT_PUBLIC_CLOUDFLARE_SITE_KEY || window._ENV_.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
      if (runtimeKey && runtimeKey.trim()) {
        siteKey = runtimeKey;
      }
    }
    
    // Fallback to build-time env (embedded at build time from secret)
    if (!siteKey) {
      siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
    }
    
    if (!siteKey) {
      return { required: false, siteKey: undefined };
    }
    
    // Check environment from both build-time and runtime config
    const env = typeof window !== 'undefined' && window._ENV_?.NEXT_PUBLIC_ENV
      ? window._ENV_.NEXT_PUBLIC_ENV
      : process.env.NEXT_PUBLIC_ENV;
    
    // Default to requiring Turnstile if site key exists (safer for production)
    // Only disable if explicitly in development
    const isDev = env === 'dev' || env === 'development';
    // If env is not set or not 'dev'/'development', require Turnstile
    const required = !isDev;
    
    return { required, siteKey };
  })();

  const isTurnstileRequired = turnstileConfig.required;
  const turnstileSiteKey = turnstileConfig.siteKey;

  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};

    if (!formData.name.trim()) {
      errors.name = t('validation.required') || 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = t('validation.required') || 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('validation.invalidEmail') || 'Invalid email address';
    }

    if (!formData.message.trim()) {
      errors.message = t('validation.required') || 'Message is required';
    }

    // Only require Turnstile if it's configured
    if (isTurnstileRequired && !turnstileToken) {
      errors.turnstile = t('validation.turnstileRequired') || 'Please complete the security verification';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Validate that we have the required ID
    if (recipientType === 'cruise' && !cruiseId) {
      setError('Cruise ID is required');
      return;
    }
    if (recipientType === 'organizer' && !organizerSlug) {
      setError('Organizer slug is required');
      return;
    }

    setError(null);
    setSuccess(false);
    setSubmitting(true);

    try {
      const formDataToSend = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
        ...(turnstileToken && { 'cf-turnstile-token': turnstileToken }),
      };

      let result: { success: boolean; error: string | null };
      
      if (recipientType === 'cruise' && cruiseId) {
        result = await submitCruiseContact(cruiseId, formDataToSend);
      } else if (recipientType === 'organizer' && organizerSlug) {
        result = await submitOrganizerContact(organizerSlug, formDataToSend);
      } else {
        throw new Error('Invalid form configuration');
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }

      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTurnstileToken(null);
      
      if (onSuccess) {
        onSuccess();
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const getTitle = () => {
    if (recipientType === 'organizer') {
      return recipientName
        ? t('common.ctaForm.contactOrganizer', { name: recipientName }) || `Contact ${recipientName}`
        : t('common.ctaForm.contactOrganizerDefault') || 'Contact Organizer';
    } else {
      return recipientName
        ? t('common.ctaForm.contactCruise', { name: recipientName }) || `Contact about ${recipientName}`
        : t('common.ctaForm.contactCruiseDefault') || 'Contact about this Cruise';
    }
  };

  const getDescription = () => {
    if (recipientType === 'organizer') {
      return t('common.ctaForm.organizerDescription') || 'Fill out the form below to contact the organizer. We will forward your message to them.';
    } else {
      return t('common.ctaForm.cruiseDescription') || 'Fill out the form below to contact the organizer about this cruise. We will forward your message to them.';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 mt-6">
      <h3 className="text-xl font-bold mb-2 text-slate-900 dark:text-slate-100">
        {getTitle()}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {getDescription()}
      </p>

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4 text-green-700 dark:text-green-300 text-sm">
          {t('common.ctaForm.success') || 'Your message has been sent successfully! We will get back to you soon.'}
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('common.ctaForm.name') || 'Name'} <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (fieldErrors.name) {
                setFieldErrors({ ...fieldErrors, name: undefined });
              }
            }}
            required
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-100 transition-all outline-none ${
              fieldErrors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
          />
          {fieldErrors.name && (
            <p className="text-sm text-red-500 mt-1">{fieldErrors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('common.ctaForm.email') || 'Email'} <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (fieldErrors.email) {
                setFieldErrors({ ...fieldErrors, email: undefined });
              }
            }}
            required
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-100 transition-all outline-none ${
              fieldErrors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
          />
          {fieldErrors.email && (
            <p className="text-sm text-red-500 mt-1">{fieldErrors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {t('common.ctaForm.message') || 'Message'} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => {
              setFormData({ ...formData, message: e.target.value });
              if (fieldErrors.message) {
                setFieldErrors({ ...fieldErrors, message: undefined });
              }
            }}
            required
            rows={6}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-800 dark:text-slate-100 transition-all outline-none resize-y ${
              fieldErrors.message ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
          />
          {fieldErrors.message && (
            <p className="text-sm text-red-500 mt-1">{fieldErrors.message}</p>
          )}
        </div>

        {/* Cloudflare Turnstile */}
        {mounted && isTurnstileRequired && turnstileSiteKey && (
          <div className="flex flex-col items-center mt-4">
            <Turnstile
              siteKey={turnstileSiteKey}
              onSuccess={(token) => {
                setTurnstileToken(token);
                setFieldErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.turnstile;
                  return newErrors;
                });
              }}
              onError={() => {
                setTurnstileToken(null);
                setFieldErrors(prev => ({
                  ...prev,
                  turnstile: t('validation.turnstileError') || 'Security verification failed. Please try again.',
                }));
              }}
              onExpire={() => {
                setTurnstileToken(null);
                setFieldErrors(prev => ({
                  ...prev,
                  turnstile: t('validation.turnstileExpired') || 'Security verification expired. Please complete it again.',
                }));
              }}
            />
            {fieldErrors.turnstile && (
              <p className="text-sm text-red-500 mt-2">{fieldErrors.turnstile}</p>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Spinner /> : <SendIcon />}
          {submitting
            ? t('common.ctaForm.sending') || 'Sending...'
            : t('common.ctaForm.send') || 'Send Message'}
        </button>
      </form>
    </div>
  );
}

